// Import the canonical `RenderFormData` from `slices/internals` directly,
// not via the store entry point — `render-form.store.ts` imports
// `slices/draft-persistence.slice.ts` which imports this file, so going
// through the store closes a madge cycle even with `import type`.
import type { RenderFormData, TechSettingsData } from '../model/slices/internals';
import { SHARED_TECH_SETTINGS_KEYS } from '../model/slices/internals';
import type { RenderMode } from '@/types/render';
import { EnvironmentSettingEnum } from '@/shared/constants';
import { getCurrentUserScope } from '@/shared/lib/user-scoped-storage';
import { migrateLegacyProductPicks } from './brand-tokens';

/**
 * Refine without any user-supplied intent. Refine generates only the delta
 * the user describes, so an empty form would render an identical copy — the
 * UI blocks Generate and prompts the user to add something. Always false
 * for `'explore'` (Explore tolerates a fully default form).
 *
 * The TECHNICAL group (`render_model_family`, `render_effort`,
 * `is_extended_thinking`, the render style — `output_style_id` AND its custom
 * file-based form `output_project_reference_ids` — and `is_hires`) is
 * intentionally NOT part of "intent": it describes how to generate, not what
 * to change, and is SHARED between Explore and Refine — a value carried over
 * from Explore (or restored from the sticky slot) must never unblock an
 * otherwise-blank Refine (it would render a near-copy). Output references are
 * therefore excluded here, exactly like `output_style_id`.
 */
export function isEmptyRefine(form: RenderFormData): boolean {
  if (form.mode !== 'refine') return false;
  const promptConcept = form.prompts?.prompt_concept ?? '';
  const promptAngle = form.prompts?.prompt_angle ?? '';
  return (
    promptConcept === '' &&
    promptAngle === '' &&
    (form.style_ids === null || form.style_ids.length === 0) &&
    (form.material_ids === null || form.material_ids.length === 0) &&
    (form.colors === null || form.colors.length === 0) &&
    // Naming a real product is intent as concrete as it gets — "put Hardie
    // Dutch lap on it" is a change to render, not a near-copy of the parent.
    (form.brand_selections === null || form.brand_selections.length === 0) &&
    form.environment_setting === EnvironmentSettingEnum.NO_CHANGE &&
    (form.style_project_reference_ids === null ||
      form.style_project_reference_ids.length === 0) &&
    (form.material_project_reference_ids === null ||
      form.material_project_reference_ids.length === 0) &&
    (form.color_project_reference_ids === null ||
      form.color_project_reference_ids.length === 0) &&
    // Refine baseline is "no refs" for style/material/color (see
    // `getRefineBaseline()` in render-form.store.ts). Flipping any of these to
    // "all refs" is a user-supplied delta and unblocks Generate.
    form.use_auto_style_refs === false &&
    form.use_auto_material_refs === false &&
    form.use_auto_color_refs === false &&
    (form.controls === null || form.controls.length === 0) &&
    // Areas marked on the image carry their own instructions.
    (form.edit_regions === null || form.edit_regions.length === 0)
  );
}

/**
 * Utilities for persisting render settings drafts to localStorage.
 *
 * Two pieces are persisted side-by-side per (concept, angle, render):
 *   - Mode-keyed drafts — the DESIGN form state under each mode (Explore +
 *     Refine stay independent so flipping back keeps separate prompts and
 *     overrides). The technical group is stripped before write.
 *   - Shared technical slot — the TECHNICAL group (model, effort, extended
 *     thinking, output style, Hi-Res). ONE value per logged-in user, shared
 *     across every concept/angle/render AND both modes: changing Hi-Res
 *     anywhere must show everywhere, so it's keyed by the user scope ALONE —
 *     no mode, concept, angle, or render suffix.
 *
 * The Explore/Refine mode itself is NOT persisted — it is ephemeral and
 * re-derived on every context entry (see `initializeFormData`).
 *
 * Mirrors Apple `RenderDraftManager.swift` and Android `RenderDraftManager.kt`.
 */

const STORAGE_PREFIX = 'render-settings-draft:';
const TECH_PREFIX = 'render-settings-tech:';
/** Legacy active-mode pointer prefix — purge-only (see below). */
const LEGACY_MODE_PREFIX = 'render-settings-mode:';

const draftPrefix = (): string =>
  `${getCurrentUserScope()}:${STORAGE_PREFIX}`;
/**
 * Full key for the per-user technical-settings slot. NO concept/angle/render
 * or mode suffix — the technical group is a SINGLE app-wide value per
 * logged-in user (the `getCurrentUserScope()` prefix is the only segment that
 * varies, giving per-user isolation on a shared device). The trailing
 * separator from the prefix builder is trimmed so the key is the bare
 * `{scope}:render-settings-tech`.
 */
const techKey = (): string =>
  `${getCurrentUserScope()}:${TECH_PREFIX}`.replace(/:$/, '');

/**
 * One-shot (per session) cleanup of two now-defunct key families, scanning
 * across user scopes (both prefixes are embedded mid-key):
 *   1. Legacy active-mode pointer keys — written but never read after the
 *      ephemeral-Explore pivot (the displayed mode is re-derived on every
 *      load), so the API was removed.
 *   2. Stale per-context technical-settings keys — superseded by the single
 *      app-wide per-user tech slot (see {@link techKey}).
 * The purge stops the already-written keys from lingering in localStorage
 * forever.
 */
let legacyModePointersPurged = false;
export function purgeLegacyActiveModePointers(): void {
  if (legacyModePointersPurged || typeof window === 'undefined') return;
  legacyModePointersPurged = true;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.includes(LEGACY_MODE_PREFIX)) {
        keysToRemove.push(key);
      }
      // Stale per-context tech keys from before the "single app-wide tech
      // slot" change: `{scope}:render-settings-tech:{concept}[:{angle}[:
      // {render}]]` — i.e. the prefix (which itself ends in `:`) followed by
      // at least one context char. The new per-user slot is the bare
      // `{scope}:render-settings-tech` with no suffix, so it is left intact.
      // Values are NOT migrated — the user's most recent edit re-seeds the
      // global slot on the next draft save.
      else if (key && new RegExp(`${TECH_PREFIX}.`).test(key)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to purge legacy active-mode pointers:', error);
  }
}

/**
 * Context-only key (no mode) used internally by the store to track the
 * current concept/angle/render — independent of which mode draft is loaded.
 * Storage keys all include `:mode` and use {@link getDraftKey} instead.
 */
export function getContextKey(
  conceptId: string,
  angleId?: string,
  renderId?: string
): string {
  const prefix = draftPrefix();
  if (angleId && renderId) {
    return `${prefix}${conceptId}:${angleId}:${renderId}`;
  }
  if (angleId) {
    return `${prefix}${conceptId}:${angleId}`;
  }
  return `${prefix}${conceptId}`;
}

/**
 * Generate localStorage key for a concept/angle/render/mode combination.
 *
 * Key shapes (mode is always appended last):
 * - `{prefix}{conceptId}:{angleId}:{renderId}:{mode}` — carousel-selected source render
 * - `{prefix}{conceptId}:{angleId}:{mode}` — angle has no render yet
 * - `{prefix}{conceptId}:{mode}` — main angle
 */
export function getDraftKey(
  conceptId: string,
  angleId?: string,
  renderId?: string,
  mode: RenderMode = 'explore'
): string {
  const prefix = draftPrefix();
  if (angleId && renderId) {
    return `${prefix}${conceptId}:${angleId}:${renderId}:${mode}`;
  }
  if (angleId) {
    return `${prefix}${conceptId}:${angleId}:${mode}`;
  }
  return `${prefix}${conceptId}:${mode}`;
}

/**
 * Deep equality check for two values
 * Handles arrays, objects, null, undefined, and primitives
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;

  // Handle null/undefined
  if (a == null || b == null) return a === b;

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    const aKeys = Object.keys(a as object);
    const bKeys = Object.keys(b as object);

    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every((key) =>
      deepEqual(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key]
      )
    );
  }

  return false;
}

/**
 * Normalize null vs empty array - treat them as equivalent
 */
function normalizeValue(value: unknown): unknown {
  if (Array.isArray(value) && value.length === 0) return null;
  return value;
}

/**
 * Remove fields from draft that match server settings
 * This keeps localStorage lean by only storing actual changes
 *
 * @param draft - User's draft settings (may include unchanged fields)
 * @param serverSettings - Current settings from server
 * @returns Cleaned draft with only changed fields
 */
export function cleanupMatchingFields(
  draft: Partial<RenderFormData>,
  serverSettings: Partial<RenderFormData>
): Partial<RenderFormData> {
  const cleaned: Partial<RenderFormData> = {};

  for (const key in draft) {
    const draftValue = normalizeValue(draft[key as keyof RenderFormData]);
    const serverValue = normalizeValue(
      serverSettings[key as keyof RenderFormData]
    );

    // Only keep the field if it differs from server
    if (!deepEqual(draftValue, serverValue)) {
      cleaned[key as keyof RenderFormData] = draft[
        key as keyof RenderFormData
      ] as never;
    }
  }

  return cleaned;
}

/**
 * Save draft settings to localStorage
 * Automatically cleans up fields that match server settings
 *
 * @param conceptId - ID of the concept
 * @param angleId - Optional ID of the derivative angle
 * @param draftData - Draft form data (only changed fields)
 * @param serverSettings - Current server settings for comparison
 */
export function saveDraft(
  conceptId: string,
  angleId: string | undefined,
  renderId: string | undefined,
  mode: RenderMode,
  draftData: Partial<RenderFormData>,
  serverSettings: Partial<RenderFormData>
): void {
  try {
    const key = getDraftKey(conceptId, angleId, renderId, mode);

    // Clean up fields that match server settings
    const cleanedDraft = cleanupMatchingFields(draftData, serverSettings);

    // The shared technical group lives in the per-context tech slot (no
    // mode suffix) — never in the mode-keyed draft, where two copies could
    // drift apart. Legacy drafts still carry these fields; they apply as a
    // fallback when no tech slot exists yet.
    for (const techKey of SHARED_TECH_SETTINGS_KEYS) {
      delete cleanedDraft[techKey];
    }

    // If nothing changed, remove the draft entirely
    if (Object.keys(cleanedDraft).length === 0) {
      localStorage.removeItem(key);
      return;
    }

    // Save cleaned draft
    localStorage.setItem(key, JSON.stringify(cleanedDraft));
  } catch (error) {
    // Fail silently - localStorage might be full or disabled
    console.warn('Failed to save render settings draft:', error);
  }
}

/**
 * Load draft settings from localStorage
 */
export function loadDraft(
  conceptId: string,
  angleId?: string,
  renderId?: string,
  mode: RenderMode = 'explore'
): Partial<RenderFormData> | null {
  try {
    const key = getDraftKey(conceptId, angleId, renderId, mode);
    const stored = localStorage.getItem(key);

    if (!stored) {
      return null;
    }

    const draft = JSON.parse(stored) as Partial<RenderFormData>;
    // A draft saved before Brands became its own setting still carries its
    // product picks inside `colors`. Server snapshots are lifted onto the
    // Brands axis when loaded into the form; a restored draft replaces that
    // form state, so it needs the same lift or the picks fall back into a
    // Colors tab that no longer shows them.
    // Keys the draft never had stay absent: a partial draft only overrides
    // what it carries.
    if (draft.colors !== undefined) {
      const lifted = migrateLegacyProductPicks(
        draft.colors,
        draft.brand_selections
      );
      draft.colors = lifted.colors;
      if (
        draft.brand_selections !== undefined ||
        lifted.brand_selections !== null
      ) {
        draft.brand_selections = lifted.brand_selections;
      }
    }
    return draft;
  } catch (error) {
    // Invalid JSON or localStorage disabled
    console.warn('Failed to load render settings draft:', error);
    return null;
  }
}

/**
 * Clear draft settings from localStorage
 */
export function clearDraft(
  conceptId: string,
  angleId?: string,
  renderId?: string,
  mode: RenderMode = 'explore'
): void {
  try {
    const key = getDraftKey(conceptId, angleId, renderId, mode);
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear render settings draft:', error);
  }
}

/**
 * Clear drafts for both Explore and Refine for the given context. Used after
 * a successful render so the next session starts fresh.
 *
 * The per-user technical slot is deliberately NOT cleared here — it is a
 * single app-wide value (model/effort/extended-thinking/output-style/Hi-Res)
 * the user carries across every concept and session. Wiping it on each render
 * would reset the engine/output preference the user just chose.
 */
export function clearDraftAllModes(
  conceptId: string,
  angleId?: string,
  renderId?: string
): void {
  clearDraft(conceptId, angleId, renderId, 'explore');
  clearDraft(conceptId, angleId, renderId, 'refine');
}

/**
 * Clear all mode-keyed draft settings for a specific concept (including all
 * angles and renders). Useful when deleting a concept or after a successful
 * render.
 *
 * The technical group is NOT touched here — it is a single app-wide per-user
 * slot ({@link techKey}), shared across every concept, so deleting one
 * concept must never reset the engine/output choices the user carries
 * everywhere.
 *
 * @param conceptId - ID of the concept
 */
export function clearAllConceptDrafts(conceptId: string): void {
  try {
    const draftConceptPrefix = `${draftPrefix()}${conceptId}`;
    const keysToRemove: string[] = [];

    // Find all mode-keyed draft keys for this concept
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(draftConceptPrefix)) {
        keysToRemove.push(key);
      }
    }

    // Remove them
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to clear concept drafts:', error);
  }
}

// ---------------------------------------------------------------------------
// Shared technical settings slot
// ---------------------------------------------------------------------------

/**
 * Storage key for the per-user technical-settings slot. NO concept/angle/
 * render or mode suffix — the technical group (model family, effort, extended
 * thinking, output style, Hi-Res) is ONE value per logged-in user, applied
 * app-wide across every concept and both Explore/Refine modes.
 */
export function getTechSettingsKey(): string {
  return techKey();
}

/**
 * Persist the per-user technical group. Written on every draft save as a FULL
 * five-field object (not a delta) — the slot, once written, always wins over
 * whatever the mode draft or baseline carried, and applies to every concept
 * the user opens.
 */
export function saveTechSettings(tech: TechSettingsData): void {
  try {
    localStorage.setItem(techKey(), JSON.stringify(tech));
  } catch (error) {
    console.warn('Failed to save render tech settings:', error);
  }
}

/**
 * Read the per-user technical group. `null` = nothing edited yet (or a legacy
 * install) — the caller keeps the mode draft's / baseline's own values
 * (legacy mode drafts still carry tech fields as the pre-split fallback).
 */
export function loadTechSettings(): TechSettingsData | null {
  try {
    const stored = localStorage.getItem(techKey());
    if (!stored) return null;
    return JSON.parse(stored) as TechSettingsData;
  } catch (error) {
    console.warn('Failed to load render tech settings:', error);
    return null;
  }
}

export function clearTechSettings(): void {
  try {
    localStorage.removeItem(techKey());
  } catch (error) {
    console.warn('Failed to clear render tech settings:', error);
  }
}
